'use client';

import { useState, useMemo } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '../../../../../components/styled/button';
import { Text } from '../../../../../components/styled/text';
import { Textarea } from '../../../../../components/styled/textarea';
import { Input } from '../../../../../components/styled/input';
import { IconButton } from '../../../../../components/icon-button';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';

export type EditToolArgsFormProps = {
  onClose: () => void;
  onSubmit: (editedArgs: Record<string, unknown>) => void;
  toolName: string;
  args: Record<string, unknown>;
  hiddenFields?: string[];
  headerActions?: React.ReactNode;
};
const formatLabel = (key: string): string => {
  return key
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const isLongValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.length > 100 || value.includes('\n');
  }

  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value, null, 2).length > 100;
  }

  return false;
};

const stringifyValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const parseValue = (value: string, originalValue: unknown): unknown => {
  if (typeof originalValue === 'string') {
    return value;
  }

  if (typeof originalValue === 'number') {
    const num = Number(value);

    return isNaN(num) ? value : num;
  }

  if (typeof originalValue === 'boolean') {
    return value === 'true';
  }

  if (typeof originalValue === 'object' && originalValue !== null) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
};

export function EditToolArgsForm({
  onClose,
  onSubmit,
  toolName,
  args,
  hiddenFields = [],
  headerActions,
}: EditToolArgsFormProps) {
  const t = useTranslations('designSystem.chat.editToolArgsModal');
  const editableArgs = useMemo(() => {
    return Object.fromEntries(
      Object.entries(args).filter(([key]) => !hiddenFields.includes(key)),
    );
  }, [args, hiddenFields]);
  const initialValues = useMemo(() => {
    const values: Record<string, string> = {};

    Object.entries(editableArgs).forEach(([key, value]) => {
      values[key] = stringifyValue(value);
    });

    return values;
  }, [editableArgs]);
  const [editedValues, setEditedValues] =
    useState<Record<string, string>>(initialValues);
  const handleValueChange = (key: string, value: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    const parsedArgs: Record<string, unknown> = {};

    Object.entries(editedValues).forEach(([key, value]) => {
      parsedArgs[key] = parseValue(value, editableArgs[key]);
    });
    hiddenFields.forEach((field) => {
      if (Object.hasOwn(args, field)) {
        parsedArgs[field] = args[field];
      }
    });
    onSubmit(parsedArgs);
    onClose();
  };

  const handleClose = () => {
    setEditedValues(initialValues);
    onClose();
  };

  const argEntries = Object.entries(editableArgs);

  return (
    <VStack gap={4} padding={4} h="full">
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <Text fontSize="sm" fontWeight="bold">
          {t('title', { toolName: formatLabel(toolName) })}
        </Text>
        <HStack gap={1}>
          {headerActions}
          <IconButton
            variant="ghost"
            size="xs"
            onClick={handleClose}
            aria-label={t('close')}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>
      </HStack>

      <Text fontSize="xs" color="fg.muted">
        {t('description')}
      </Text>

      <VStack gap={3} width="100%" flex={1} overflowY="auto" paddingRight={2}>
        {argEntries.map(([key, originalValue]) => {
          const isLong = isLongValue(originalValue);

          return (
            <VStack key={key} gap={1} width="100%" alignItems="flex-start">
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                {formatLabel(key)}
              </Text>
              {isLong ? (
                <Textarea
                  value={editedValues[key] || ''}
                  onChange={(e) => handleValueChange(key, e.target.value)}
                  rows={4}
                  resize="vertical"
                  fontSize="xs"
                />
              ) : (
                <Input
                  value={editedValues[key] || ''}
                  onChange={(e) => handleValueChange(key, e.target.value)}
                  size="sm"
                  fontSize="xs"
                />
              )}
            </VStack>
          );
        })}
      </VStack>

      <HStack gap={2} justifyContent="flex-end" width="100%">
        <Button variant="ghost" size="xs" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button size="xs" onClick={handleSubmit}>
          {t('submit')}
        </Button>
      </HStack>
    </VStack>
  );
}
